import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { ClientEntryStore } from '../stores/client-entry-store';
import { ClientEntry } from '../components/client/client-entry';
import { UserStore } from '../stores/user-store';
import { ClientDisplay } from '../components/client/client-display';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { scrollPanelIntoView } from '../utils/scrollPanelIntoView';

/**
 * Asking for Add Client while already standing on it.
 *
 * "Add Client" is a plain link to /client/add, so clicking it from that page
 * pushes the same route: React Router hands down a fresh location.key and
 * nothing else changes, the container never unmounts, and componentDidMount
 * does not run again. A panel the practitioner had collapsed stayed collapsed,
 * and the page stayed wherever it was scrolled, so the menu item looked
 * pressed and did nothing. Same shape as the bug already fixed for Add Test
 * Results, and answered the same way.
 *
 * Deliberately does NOT reset the client being entered. componentDidMount
 * does that on a real arrival, but here the practitioner may be halfway
 * through typing one, and a menu click that silently emptied the form would
 * cost them the work.
 *
 * Exported for its test: @inject wraps the component, so the class itself is
 * not reachable from outside this module.
 */
export function reopenAddClientPanelOnRenavigation(
  previousLocation: any,
  location: any,
  clientEntryStore: ClientEntryStore,
): void {
  if (previousLocation?.key === location?.key) {
    return;
  }
  clientEntryStore.setExpandAddClient(true);
  // The menu lives in the sticky header and is reachable from anywhere, so
  // this can be clicked from a page scrolled well down. Expanding a form the
  // practitioner cannot see reads as one more click that did nothing.
  scrollPanelIntoView('client-entry-panel');
}

interface ClientEntryContainerProps {
  userProfileUIStore: UserProfileUIStore;
  clientEntryStore?: ClientEntryStore;
  userStore?: UserStore;
  bloodworkEntryStore: BloodworkEntryStore;
  location?: any;
  history?: any;
}

@inject('userProfileUIStore', 'clientEntryStore', 'userStore', 'bloodworkEntryStore')
@observer
// Copied from ClientEntryContainer due to issues when user is editing a client and
// then clicks on Add Client.  The previous client wasn't being reset.
export class ClientAddContainer extends React.Component<ClientEntryContainerProps, {}> {

  componentDidMount() {
    const { clientEntryStore, userStore } = this.props;
    userStore.loadCurrentUser();
    clientEntryStore.loadCurrentClients();
    clientEntryStore.resetCurrentClient();
    clientEntryStore.setExpandAddClient(true);
  }

  componentDidUpdate(previousProps: ClientEntryContainerProps) {
    reopenAddClientPanelOnRenavigation(
      previousProps.location,
      this.props.location,
      this.props.clientEntryStore,
    );
  }

  render() {
    const { userProfileUIStore, clientEntryStore, userStore, bloodworkEntryStore } = this.props;
    return (
      <div className="section">
        <ClientEntry
          client={clientEntryStore.currentClient}
          currentUser={userStore.currentUser}
          clientEntryStore={clientEntryStore}
          saveOrUpdate={() => clientEntryStore.saveOrUpdate()}
          expandAddClient={clientEntryStore.expandAddClient}
          defaultUnits={userProfileUIStore.currentUserProfile?.unitSystem}
          history={this.props.history}
        />
        <ClientDisplay
          currentUserProfile={userProfileUIStore.currentUserProfile}
          clients={clientEntryStore.clients}
          clientEntryStore={clientEntryStore}
          bloodworkEntryStore={bloodworkEntryStore}
          history={this.props.history}
        />
      </div>
    );
  }
}